/**
 * Rhythmz — VideoLayer
 * ─────────────────────
 * Real video footage (a performer, dancer, any clip) rendered as a textured
 * fullscreen plane with AUDIO-REACTIVE post-treatment — the "Excision on stage"
 * realism layer. Treatment matches the live-show references:
 *   - beat-synced strobe / brightness punch
 *   - RGB split (chromatic aberration) that spikes on drops
 *   - bass-driven displacement / glass distortion
 *   - color grade toward the generative palette so footage + visuals cohere
 *   - luma-key option (drop dark backgrounds so only the performer shows)
 *
 * The video plays as a layer; the generative engine renders behind it.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec2, vec3, vec4, uv, float, uniform, add, sub, mul, mix, step, cos } from 'three/tsl';
import type { AudioFeatures } from './AudioAnalyzer';

export class VideoLayer {
  readonly mesh: THREE.Mesh;
  private material: THREE.NodeMaterial;
  private geo: THREE.PlaneGeometry;
  private texture: THREE.VideoTexture;
  private video: HTMLVideoElement;

  // reactive uniforms (engine updates each frame)
  uBeat = uniform(0);
  uBass = uniform(0);
  uLevel = uniform(0);
  uStrobe = uniform(0);        // 0..1 strobe intensity (set on beat)
  uRGBSplit = uniform(0);      // chromatic aberration amount (spikes on drops)
  uGradeMix = uniform(0.4);    // how strongly to color-grade toward palette
  uLumaKey = uniform(0);       // 0..1 dark-background suppression
  uCol0 = uniform(new THREE.Color(0.85, 0.2, 0.55));
  uCol1 = uniform(new THREE.Color(0.2, 0.55, 1.0));
  private visible = true;

  private constructor(video: HTMLVideoElement, texture: THREE.VideoTexture) {
    this.video = video;
    this.texture = texture;
    this.geo = new THREE.PlaneGeometry(2, 2);
    this.material = new THREE.NodeMaterial();
    this.material.fragmentNode = this.buildShader();
    this.material.depthTest = false;
    this.material.depthWrite = false;
    this.material.transparent = true;
    this.mesh = new THREE.Mesh(this.geo, this.material);
    this.mesh.frustumCulled = false;
    // Render after the generative plane (which is at z=0); place this at z=0.5
    // but use renderOrder + transparent so it draws on top when desired, or
    // negative renderOrder to sit behind. Default: in front.
    this.mesh.renderOrder = 1;
  }

  /** Load a video file and build the layer. */
  static async fromFile(file: File): Promise<VideoLayer> {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.loop = true;
    video.muted = true;          // muted so it can autoplay; audio comes from a separate path if needed
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    await video.play().catch(() => {});
    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    return new VideoLayer(video, texture);
  }

  /** The audio-reactive fragment shader. */
  private buildShader() {
    const tex = this.texture;
    const sampleTex = (uvCoord: any) => vec3(texture(tex, uvCoord));
    return Fn(() => {
      const baseUV = uv();
      // 1. Bass-driven displacement (glass/distortion wobble).
      const wobble = mul(this.uBass, float(0.015));
      const wobbleUV = add(baseUV, vec2(
        cos(add(mul(baseUV.y, float(40.0)), mul(this.uBass, float(20.0)))).mul(wobble),
        cos(add(mul(baseUV.x, float(30.0)), mul(this.uBass, float(15.0)))).mul(wobble),
      ));
      // 2. RGB split (chromatic aberration) — spikes on drops via uRGBSplit.
      const split = this.uRGBSplit;
      const r = sampleTex(add(wobbleUV, vec2(split, 0.0)));
      const g = sampleTex(wobbleUV);
      const b = sampleTex(sub(wobbleUV, vec2(split, 0.0)));
      let col: any = vec3(r.x, g.y, b.z);

      // 3. Luma-key: suppress dark background so only the bright subject shows
      //    (makes a performer pop over the generative visuals).
      const lum = dot(vec3(float(0.299), float(0.587), float(0.114)), col);
      const lumaMask = mix(float(1.0), step(sub(float(1.0), this.uLumaKey), lum), this.uLumaKey);
      col = mul(col, lumaMask);

      // 4. Color grade toward the generative palette (cohesion).
      const gradeA = col;
      const gradeB = mul(col, mix(this.uCol0.value, this.uCol1.value, lum)); // tint by brightness
      col = mix(gradeA, gradeB, this.uGradeMix);

      // 5. Beat strobe: brightness punch on beat + contrast lift.
      const strobe = add(float(1.0), mul(this.uStrobe, float(0.6)));
      col = mul(col, strobe);

      // 6. Contrast/saturation push for the "produced" look.
      const luma2 = dot(vec3(float(0.299), float(0.587), float(0.114)), col);
      col = add(luma2, mul(sub(col, luma2), float(1.25))); // saturation

      return vec4(col, lumaMask);
    })();
  }

  /** Engine calls this each frame with current audio features. */
  updateAudio(af: AudioFeatures): void {
    this.uBeat.value = af.beat;
    this.uBass.value = af.bass;
    this.uLevel.value = af.level;
    // strobe punches on a beat then decays naturally (beat itself decays)
    this.uStrobe.value = af.beat;
    // RGB split spikes when bass + beat are both high (a "drop")
    this.uRGBSplit.value = (af.bass * 0.6 + af.beat * 0.4) * 0.01;
  }

  /** Set the palette the footage grades toward (cohere with current preset). */
  setPalette(c0: THREE.Color, c1: THREE.Color): void {
    this.uCol0.value.copy(c0);
    this.uCol1.value.copy(c1);
  }

  setGradeMix(v: number): void { this.uGradeMix.value = v; }
  setLumaKey(v: number): void { this.uLumaKey.value = v; }
  setShowBehind(behind: boolean): void { this.mesh.renderOrder = behind ? -1 : 1; }

  get isVisible(): boolean { return this.visible; }
  setVisible(v: boolean): void { this.visible = v; this.mesh.visible = v; }

  dispose(): void {
    URL.revokeObjectURL(this.video.src);
    this.video.pause();
    this.video.src = '';
    this.texture.dispose();
    this.geo.dispose();
    this.material.dispose();
  }
}

// helpers used inside Fn
import { dot, texture } from 'three/tsl';
